// The two layers every extractor needs after `extract`, and the two rules that
// were bugs before they were rules.
//
// An extractor is three stages:
//
//   extract(turns)            one request body → its ops          per-domain
//   collectWindow(prompts)    many bodies → the window's real ops  HERE
//   groupActivity(flatOps)    ops → the units a reader sees        HERE
//
// The middle and last stages look trivial and are not. Both of the following
// shipped as defects in the first extractor built this way, and neither is
// domain-specific, so every extractor written without them rediscovers them
// against production data.
//
// RULE 1 — FIRST SIGHTING, BUT UPGRADE IN PLACE.
// Provider request bodies are CUMULATIVE: each request carries the whole
// conversation so far, so one tool call reappears in every later body of its
// session. Deduping to the first sighting is therefore mandatory — without it
// every op is counted once per subsequent request, inflating counts by the
// session length.
//
// But first-sighting exists to stop double-counting, NOT to prefer the
// least-informed copy. A run that writes its output to a file is first seen
// BEFORE anything read that file back, so the earliest body structurally cannot
// know its output. Keeping the earliest copy therefore discards the only version
// that knows what happened. Upgrade the stored op in place when a later sighting
// knows more, while keeping provenance (which request, which timestamp) from the
// first — that is when the work actually happened.
//
// RULE 2 — THE GROUPING KEY IS NOT THE DISPLAY LABEL.
// A key must be unique per unidentified thing: two runs against `$dump` are
// almost certainly two different dumps, and collapsing them invents one unit of
// work that never existed. A label is read by a person, and a key that satisfies
// uniqueness — a tool-use id, a synthetic hash — names nothing to anyone. They
// are different jobs and conflating them produces a screen full of rows titled
// `unresolved · toolu_01NV9fjcCGFEmq19RNBiMsSM`.

// Fields the collect layer stamps onto every op. Extractors must not use these
// names for their own parsed values.
const PROVENANCE = ["promptId", "sessionId", "endpointId", "endpointName", "ts"];

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

/**
 * Flatten per-body extractions into the window's real ops.
 *
 * @param {object[]} promptEvents  {promptId, sessionId, endpointId, endpointName, ts, ops[]}
 * @param {object} opts
 *   informedness  (op) => number   how much this sighting knows. A later sighting
 *                                  scoring higher REPLACES the stored one's
 *                                  parsed fields. Default: length of any string
 *                                  field, which approximates "captured more".
 *                                  Give a real one when the op has a better
 *                                  signal (checkpoints reached, output present).
 *   identity      (op) => string   what makes an op the same op across bodies.
 *                                  Default: `op.key`, which extractors set to the
 *                                  tool-use id.
 */
export function collectWindowOps(promptEvents, { informedness = defaultInformedness, identity = (op) => op.key } = {}) {
  const sorted = [...(promptEvents || [])].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const at = new Map();
  const flat = [];

  for (const p of sorted) {
    for (const op of p.ops || []) {
      // Scoped by session: the same tool-use id in two sessions is two calls.
      const dedupe = `${p.sessionId || ""}|${identity(op)}`;
      const prior = at.get(dedupe);
      if (prior !== undefined) {
        if (informedness(op) > informedness(flat[prior])) {
          // Parsed fields from the better-informed sighting; provenance from the
          // first, because that is when the work happened.
          const keep = {};
          for (const f of PROVENANCE) keep[f] = flat[prior][f];
          flat[prior] = { ...flat[prior], ...op, ...keep };
        }
        continue;
      }
      at.set(dedupe, flat.length);
      flat.push({
        ...op,
        promptId: p.promptId,
        sessionId: p.sessionId || "",
        endpointId: p.endpointId || "",
        endpointName: p.endpointName || "",
        ts: p.ts ?? null,
      });
    }
  }
  return flat;
}

// Longest string field. A crude proxy for "captured more", and enough that the
// default never prefers an empty copy over a populated one.
function defaultInformedness(op) {
  let n = 0;
  for (const v of Object.values(op || {})) if (typeof v === "string") n += v.length;
  return n;
}

/**
 * Group ops into the units a reader sees.
 *
 * @param {object[]} flatOps
 * @param {object} opts
 *   key    (op) => string    grouping key. REQUIRED. Must be unique per
 *                           unidentified thing — see RULE 2. `distinctKey` below
 *                           is the helper for that.
 *   label  (op) => string    display label. Defaults to the key, which is only
 *                           correct when the key is itself human-readable.
 *   seed   () => object      initial per-group accumulator for domain fields.
 *   fold   (acc, op) => void merge one op's domain fields into the accumulator.
 *   finish (acc) => void     roll up the accumulator once every op is folded.
 *                            For facts that only exist across the whole group — a
 *                            max, a spread, a summary line. Computing those inside
 *                            `fold` means recomputing them per op and, worse,
 *                            leaving a half-formed value behind if the last op is
 *                            filtered; this runs exactly once per group.
 */
export function groupActivity(flatOps, { key, label, seed, fold, finish } = {}) {
  if (typeof key !== "function") throw new TypeError("groupActivity needs a key(op)");
  const groups = new Map();
  const actors = new Map();

  for (const op of flatOps || []) {
    const k = String(key(op) ?? "");
    if (!k) throw new Error("groupActivity: key(op) returned nothing — an op with no key cannot be grouped");
    let g = groups.get(k);
    if (!g) {
      g = {
        key: k,
        // Defaulting the label to the key is safe ONLY when the key is readable.
        // A key built for uniqueness (a tool-use id, a hash) is not, which is why
        // `label` exists and why this default is worth noticing.
        label: label ? String(label(op)) : k,
        ops: [],
        sessions: new Set(),
        endpoints: new Set(),
        firstTs: null,
        lastTs: null,
        ...(seed ? seed() : {}),
      };
      groups.set(k, g);
    }
    g.ops.push(op);
    if (op.sessionId) g.sessions.add(op.sessionId);
    if (op.endpointId) g.endpoints.add(op.endpointId);
    const t = tsOf(op.ts);
    if (t != null) {
      g.firstTs = g.firstTs == null ? t : Math.min(g.firstTs, t);
      g.lastTs = g.lastTs == null ? t : Math.max(g.lastTs, t);
    }
    if (fold) fold(g, op);

    const aid = op.endpointId || op.endpointName;
    if (aid) {
      let a = actors.get(aid);
      if (!a) { a = { id: op.endpointId || null, name: op.endpointName || null, ops: 0, groups: new Set() }; actors.set(aid, a); }
      a.ops += 1;
      a.groups.add(k);
    }
  }

  if (finish) for (const g of groups.values()) finish(g);

  const list = [...groups.values()]
    .map((g) => ({ ...g, sessions: g.sessions.size, endpoints: g.endpoints.size }))
    .sort((a, b) => (b.lastTs ?? 0) - (a.lastTs ?? 0));

  return {
    groups: list,
    actors: [...actors.values()]
      .map((a) => ({ ...a, groups: a.groups.size }))
      .sort((a, b) => b.ops - a.ops),
    totals: { ops: (flatOps || []).length, groups: list.length },
  };
}

/**
 * A grouping key for something whose identity is UNRESOLVED.
 *
 * Use this instead of a shared sentinel. `"(no target)"` for every unidentified
 * run collapses them into one bucket that reads as a single piece of work — on
 * the first extractor built this way it swallowed seven of eight runs and the
 * lens reported one investigation where there were seven.
 */
export const distinctKey = (base, op) => `${base || "unresolved"} · ${op.key}`;
